// The All accounts tab's row action (go-live), decided from `/api/me`'s three capability flags only.
// No role name is read here: the server decides which flag a role holds (`account_actions_for`).
//
//   can_route_directly            "Route to…"        POST /api/accounts/assign (the approver's route)
//   can_request_route_for_others  "Request routing"  POST /api/accounts/request-route
//   can_request_for_self          "Request"          POST /api/accounts/claim
//
// At most one flag is true; a viewer holds none and gets no action column at all. Only an
// available (bank, not retired) account can be acted on; every other row says why not.
//
// Pure, and imported by `node --test`.

import type { BankRequest, BookAccount, Viewer } from '../api/types';
import type { OwnerState } from './allAccountsFilters.ts';
import { declinedForViewer, isApprovedRequest, isPendingRequest, requestMark } from './bankRequest.ts';

export type RowActionKind = 'route' | 'request_route' | 'request';

export type ActionCapabilities = Pick<Viewer, 'can_route_directly' | 'can_request_route_for_others' | 'can_request_for_self'>;

// The one action this viewer has, or null for none. Routing wins if a payload ever sets two.
export function actionKindFor(capabilities: ActionCapabilities | null | undefined): RowActionKind | null {
  if (capabilities?.can_route_directly) return 'route';
  if (capabilities?.can_request_route_for_others) return 'request_route';
  if (capabilities?.can_request_for_self) return 'request';
  return null;
}

export const ACTION_LABEL: Record<RowActionKind, string> = {
  route: 'Route to…',
  request_route: 'Request routing',
  request: 'Request',
};

// What the action cell shows for one row: a live button, or a muted label with the reason on hover.
export interface RowAction {
  kind: RowActionKind;
  enabled: boolean;
  label: string;
  // The hover: why it is disabled, or what the button does.
  note: string;
  // A small second line ("for Omair", "Declined"), when there is one.
  detail: string | null;
}

export interface RowActionInput {
  row: BookAccount;
  owner_state: OwnerState;
  // Who owns it, for "Owned by …".
  owner_name: string;
  request: BankRequest | null;
  viewer_email: string;
  // A strategist's display name from their address.
  name_of: (email: string) => string;
}

const ENABLED_NOTE: Record<RowActionKind, string> = {
  route: 'Choose a strategist and route it. Salesforce is written now.',
  request_route: 'Ask for this account to go to a strategist. Admin or leadership approves.',
  request: 'Ask for this account. Admin or leadership approves.',
};

function disabled(kind: RowActionKind, label: string, note: string, detail: string | null = null): RowAction {
  return { kind, enabled: false, label, note, detail };
}

function notAvailable(kind: RowActionKind, input: RowActionInput): RowAction {
  if (input.owner_state === 'retired') {
    return disabled(kind, 'Retired', 'Retired: watched, not offered. It comes back on its own when a signal wakes it.');
  }
  const owner = input.owner_name ? `Owned by ${input.owner_name}` : 'Not in the bank';
  return disabled(kind, 'Not available', `${owner}. Only bank accounts can be requested or routed.`);
}

export function rowActionFor(capabilities: ActionCapabilities | null | undefined, input: RowActionInput): RowAction | null {
  const kind = actionKindFor(capabilities);
  if (!kind) return null;

  // Only the bank's live rows are offered.
  if (input.owner_state !== 'available') return notAvailable(kind, input);
  if (input.row.competitor) return disabled(kind, 'Competitor', 'Marked as a competitor in Salesforce; never routed.');
  if (!input.row.domain) return disabled(kind, 'No domain', 'This account has no domain, so it cannot be requested or routed.');

  // A request already waiting: its mark, and for an approver where to decide it.
  const request = input.request;
  if (isPendingRequest(request) && request) {
    const mark = requestMark(request, input.viewer_email, input.name_of);
    const where = kind === 'route' ? ' Approve or decline it under Requests waiting, above.' : '';
    return disabled(kind, mark.label, mark.title + '.' + where, mark.who);
  }
  if (isApprovedRequest(request)) return disabled(kind, 'Approved ✓', 'Approved; it moves to the strategist’s book on the next sync.');

  // Declined for this viewer: say so, and let them ask again.
  const declined = kind === 'route' ? null : declinedForViewer(request, input.viewer_email);
  if (declined) {
    const reason = declined.declined_reason ? `Declined — ${declined.declined_reason}` : 'Declined';
    return { kind, enabled: true, label: `${ACTION_LABEL[kind]} again`, note: reason, detail: 'Declined' };
  }
  return { kind, enabled: true, label: ACTION_LABEL[kind], note: ENABLED_NOTE[kind], detail: null };
}

// Whether the action form needs a strategist picker, and whether its reason is required.
export function actionNeedsStrategist(kind: RowActionKind): boolean {
  return kind === 'route' || kind === 'request_route';
}

export function actionNeedsReason(kind: RowActionKind): boolean {
  return kind === 'request' || kind === 'request_route';
}

export function actionVerb(kind: RowActionKind): string {
  return kind === 'route' ? 'Route' : 'Request';
}
